import { useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { opsApi } from "../../lib/api";

/** Record what actually got submitted against what was bid. The FRS is flagged for review; the
 *  write itself stays a confirmed step in the package detail. */
export default function SwapDialog({ projectId, pkgId, item, matching, onClose, onDone }) {
  const bid = {
    manufacturer: item.current_manufacturer || item.manufacturer || "",
    model: item.current_model || item.model || "",
    description: item.current_product_name || item.product_name || "",
  };
  const [manufacturer, setManufacturer] = useState(bid.manufacturer);
  const [model, setModel] = useState("");
  const [description, setDescription] = useState("");
  const [reason, setReason] = useState("");
  const [all, setAll] = useState(true);
  const [costImpact, setCostImpact] = useState(false);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!model.trim() && !description.trim()) {
      toast.error("Give the submitted model or description");
      return;
    }
    if (!reason.trim()) {
      toast.error("Say why — the reason is what lands in the FRS revision log");
      return;
    }
    setBusy(true);
    try {
      const res = await opsApi.submittalSwap(projectId, pkgId, {
        frs_item_id: item.frs_item_id,
        frs_item_key: item.frs_item_key || "",
        bid_product: {
          manufacturer: bid.manufacturer, product_code: bid.model,
          product_description: bid.description,
        },
        submitted_product: {
          manufacturer: manufacturer.trim(), product_code: model.trim(),
          product_description: description.trim() || bid.description,
        },
        reason: reason.trim(),
        apply_to_matching: all,
        cost_impact: costImpact,
      });
      toast.success(`Swap recorded on ${res.recorded} FRS row(s) — the FRS is flagged for review`);
      onDone();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "That swap could not be recorded");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
         onClick={onClose}>
      <div className="w-full max-w-lg rounded-xl bg-white p-5 shadow-xl"
           onClick={(e) => e.stopPropagation()} data-testid="ops-submittal-swap-dialog">
        <h3 className="text-base font-semibold text-slate-900">Record a product swap</h3>
        <p className="mt-1 text-xs text-slate-500">
          What we bid stays on the record; the FRS moves to what was submitted once you confirm it
          in the package.
        </p>

        <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">
          <p className="font-semibold">{item.swapped ? "Product on the FRS now" : "Bid product"}</p>
          <p className="mt-0.5">
            {[bid.manufacturer, bid.model].filter(Boolean).join(" ") || "no product on the row"}
            {bid.description ? ` · ${bid.description}` : ""}
          </p>
          <p className="mt-0.5 text-slate-500">Room {item.room || "—"}</p>
        </div>

        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          <div>
            <label className="block text-xs font-medium text-slate-600">Submitted manufacturer</label>
            <Input value={manufacturer} onChange={(e) => setManufacturer(e.target.value)}
                   className="mt-1 h-9 text-sm" data-testid="ops-swap-manufacturer" />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600">Submitted model / part #</label>
            <Input value={model} onChange={(e) => setModel(e.target.value)}
                   className="mt-1 h-9 text-sm" data-testid="ops-swap-model" />
          </div>
        </div>

        <label className="mt-3 block text-xs font-medium text-slate-600">Submitted description</label>
        <Input value={description} onChange={(e) => setDescription(e.target.value)}
               placeholder={bid.description || ""} className="mt-1 h-9 text-sm"
               data-testid="ops-swap-description" />

        <label className="mt-3 block text-xs font-medium text-slate-600">Why</label>
        <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2}
                  className="mt-1 text-sm" data-testid="ops-swap-reason"
                  placeholder="Discontinued, GC-directed, lead time…" />

        <label className="mt-3 flex items-start gap-2 text-xs text-slate-700">
          <input type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)}
                 className="mt-0.5" data-testid="ops-swap-apply-all" />
          <span>
            Apply to every row on this package with the same bid product
            {matching > 1 ? ` (${matching} rooms)` : ""} — untick for this room only
          </span>
        </label>
        <label className="mt-2 flex items-start gap-2 text-xs text-slate-700">
          <input type="checkbox" checked={costImpact}
                 onChange={(e) => setCostImpact(e.target.checked)} className="mt-0.5"
                 data-testid="ops-swap-cost-impact" />
          <span>Not like-for-like — flag that the money needs looking at</span>
        </label>

        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose}
                  data-testid="ops-swap-cancel">Cancel</Button>
          <Button size="sm" onClick={save} disabled={busy} data-testid="ops-swap-save">
            {busy && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />} Record the swap
          </Button>
        </div>
      </div>
    </div>
  );
}
